/**
 * InsightPanel — the small KPI-grid card that lives inside the brand column.
 *
 * Prototype's `.insight-panel`. Two slots: a head (title / caption / status
 * pill) and a 2×2 grid of `InsightKpi` items. The grid reflows to a single
 * column below 480px, but the brand column itself hides earlier (960px) so
 * that almost never matters in practice.
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface InsightPanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title: React.ReactNode;
  caption?: React.ReactNode;
  /** Right-aligned chip. Pass a `<MetaPill>` or `<LivePill>` etc. */
  badge?: React.ReactNode;
}

export function InsightPanel({
  title,
  caption,
  badge,
  children,
  className,
  ...props
}: InsightPanelProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 p-4 rounded bg-paper border border-line shadow-card",
        className,
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <strong className="text-sm leading-[18px] font-semibold text-ink">{title}</strong>
          {caption ? <span className="text-xs text-muted">{caption}</span> : null}
        </div>
        {badge}
      </div>
      <div className="grid grid-cols-2 gap-x-5 gap-y-3 pt-3 border-t border-line">{children}</div>
    </div>
  );
}

export interface InsightKpiProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  detail?: React.ReactNode;
}

export function InsightKpi({ label, value, detail, className, ...props }: InsightKpiProps) {
  return (
    <div className={cn("flex flex-col gap-0.5 min-w-0", className)} {...props}>
      <span className="text-xs uppercase tracking-[0.14em] text-muted font-medium">
        {label}
      </span>
      <span className="text-base leading-[20px] font-semibold text-ink">{value}</span>
      {detail ? <span className="text-xs leading-[14px] text-muted">{detail}</span> : null}
    </div>
  );
}
